import React, { useEffect, useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Card, CardContent
} from "@mui/material";
import { styled } from "@mui/system";
import DatePicker from "react-datepicker";
import { HiRefresh } from "react-icons/hi";
import Loader from "../component/Loader";
import { getData } from "../store/utils";
import "../css/makeTable.css";
import Swal from "sweetalert2";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { FaSearch } from "react-icons/fa";
import Select from "react-select";

const StyledTableContainer = styled(TableContainer)({
  marginBottom: "20px",
  boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
  overflow: "hidden",
});

const StyledTable = styled(Table)({
  width: "100%",
  borderCollapse: "collapse",
  borderRadius: "10px",
  border: "1px solid #e0e0e0"
});

const StyledTableHead = styled(TableHead)({
    "& tr": {
        backgroundColor: "#f67555"
    }
});

const StyledTableBodyRow = styled(TableRow)({
    "&:nth-of-type(even)": {
        backgroundColor: "#fafafa"
    },
    "&:hover": {
        backgroundColor: "#f0f0f0"
    }
});

const PaymentTypeSummary = () => {
  const [paymentData, setPaymentData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isLoadingOn, setIsLoadingOn] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [todayDate, setTodayDate] = useState(null);
  const [filterData, setFilterData] = useState('');
  const [apiErrorMsg, setApiErrorMsg] = useState();

  const formatter = new Intl.NumberFormat("en-IN");

  const statusOption = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'currentWeek', label: 'Current Week' },    
    { value: 'lastWeek', label: 'Last Week' }, 
    { value: 'currentMonth', label: 'Current Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'overall', label: 'Overall' }, 
  ];

  const fetchPaymentData = async () => {
    const today = format(new Date(), "yyyy-MM-dd");
    setTodayDate(today);
    setIsLoadingOn(true);
  
    let computedFromDate = fromDate;
    let computedToDate = toDate;
  
    switch (filterData) {
      case "today":
        computedFromDate = today;
        computedToDate = today;
        break;

      case "yesterday":
        computedFromDate = format(subDays(new Date(), 1), "yyyy-MM-dd");
        computedToDate = computedFromDate;
        break;

      case "currentWeek":
        computedFromDate = format(startOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd");
        computedToDate = format(endOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd");
        break;

      case "lastWeek":
        computedFromDate = format(startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }), "yyyy-MM-dd");
        computedToDate = format(endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }), "yyyy-MM-dd");
        break;
  
      case "currentMonth":
        computedFromDate = format(startOfMonth(new Date()), "yyyy-MM-dd");
        computedToDate = format(endOfMonth(new Date()), "yyyy-MM-dd");
        break;

      case "lastMonth":
        computedFromDate = format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
        computedToDate = format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
        break;
  
      case "overall":
        computedFromDate = null;
        computedToDate = null;
        break;
    }
  
    let apiUrl = `dashboard/paymentTypeDashboard`;
    if (computedFromDate) apiUrl += `?fromDate=${computedFromDate}`;
    if (computedToDate) apiUrl += `${computedFromDate ? '&' : '?'}toDate=${computedToDate}`;
  
    try {
      const tempData = await getData(apiUrl);
      if (tempData.statusCode === 200) {
        setPaymentData(tempData.data);
        setErrorMessage("");
      } else {
        const errorMsg = tempData?.response?.data?.errors?.msg || "Something went wrong";
        setApiErrorMsg(errorMsg);
        Swal.fire({ title: "Error!", text: errorMsg });
      }
    } catch (error) {
      setApiErrorMsg("API Error");
    } finally {
      setIsLoadingOn(false);
    }
  };

  useEffect(() => {
    fetchPaymentData();
  }, [filterData, fromDate, toDate]);

  const handleFromDateChange = (date) => {
    const formattedDate = date ? format(date, "yyyy-MM-dd") : null;
    setFromDate(formattedDate);
  };
  
  const handleToDateChange = (date) => {
    const formattedDate = date ? format(date, "yyyy-MM-dd") : null;
    setToDate(formattedDate);
  };

  return (
    <div className="dashboard-container">
       <div className="centre">
            <h4 style={{ padding: '16px', textAlign: 'left' }}>Payment Type Summary</h4>
        </div>
        <div className='customHeaderTable'>
          <div className="container-fluid">
            <div className="row alignmentTable">
                <div className="col-md-2">
                    <Select 
                        defaultValue={statusOption[6]}
                        placeholder="Payment Type"
                        name="role"
                        options={statusOption}
                        onChange={(e) => setFilterData(e.value)}
                    />
                </div>
                <div className="col-md-3">
                    <label>From :</label>&nbsp;
                    <DatePicker
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        selected={fromDate ? new Date(fromDate) : null}
                        onChange={handleFromDateChange}
                        placeholderText="Select Start Date"
                        maxDate={new Date()}
                    />
                </div>
                <div className="col-md-3">
                    <label>To :</label>&nbsp;
                    <DatePicker
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        selected={toDate ? new Date(toDate) : null}
                        onChange={handleToDateChange}
                        placeholderText="Select End Date"
                        maxDate={new Date()}
                    />
                </div>
                <div className="col-md-2">
                    <button className='resetTableData' onClick={() => window.location.reload()}>
                        <HiRefresh /> Reset
                    </button>
                </div>
            </div>
          </div>
        </div>

        {isLoadingOn ? <Loader /> : (
            <div className="mt-12">
                <Card>
                    <CardContent>
                        <div className="row statusTable">
                            <div className="col-sm-12" style={{ height: '500px', overflow: 'auto' }}>
                                <StyledTableContainer component={Paper}>
                                    <StyledTable>
                                        <StyledTableHead>
                                            <TableRow>
                                                <TableCell style={{ color: '#fff' }}><b>Payment Type</b></TableCell>
                                                <TableCell style={{ color: '#fff' }}><b>Order Status</b></TableCell>
                                                <TableCell style={{ color: '#fff' }}><b>Total Orders</b></TableCell>
                                                <TableCell style={{ color: '#fff' }}><b>Total Order Amount</b></TableCell>
                                            </TableRow>
                                        </StyledTableHead>
                                        <TableBody>
                                            {paymentData.map((payment, index) => {
                                                const totalOrders = payment.orderStatusDetails.reduce((acc, item) => acc + item.total_count, 0);
                                                const totalAmount = payment.orderStatusDetails.reduce((acc, item) => acc + item.total_order_amount, 0);

                                                return (
                                                    <React.Fragment key={index}>
                                                        <TableRow>
                                                            <TableCell><b>{payment.paymentType}</b></TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell><b>{totalOrders}</b></TableCell>
                                                            <TableCell><b>{formatter.format(totalAmount)}</b></TableCell>
                                                        </TableRow>
                                                        {payment.orderStatusDetails.map((status, idx) => (
                                                            <StyledTableBodyRow key={idx}>
                                                                <TableCell></TableCell>
                                                                <TableCell>{status.orderStatus}</TableCell>
                                                                <TableCell>{status.total_count}</TableCell>
                                                                <TableCell>{formatter.format(status.total_order_amount)}</TableCell>
                                                            </StyledTableBodyRow>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableBody>
                                    </StyledTable>
                                </StyledTableContainer>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        )}
    </div>
  );
};

export default PaymentTypeSummary;